import invariant from 'tiny-invariant';

type KeyMap<Key extends string> = { [N in Key]?: any };

type MapsParams<
  Key extends string,
  Params extends ReadonlyArray<{ [N in Key]?: any } | URLSearchParams | undefined | null>,
> = {
  [I in keyof Params]: Params[I] extends KeyMap<Key> ? Params[I] : never;
};

type URLSearchParamsValueTypes<
  Params extends ReadonlyArray<{ [N in any]?: any } | URLSearchParams | undefined | null>,
> = {
  [I in keyof Params]: Params[I] extends URLSearchParams ? string : never;
}[number];

type ParamValue<
  Key extends string,
  Params extends ReadonlyArray<{ [N in Key]?: any } | undefined | null | URLSearchParams>,
  Convert extends ((value: any) => any) | undefined = undefined,
> = Exclude<
  Convert extends (value: any) => infer T
    ? T
    : Exclude<MapsParams<Key, Params>[number], undefined | null>[Key] | URLSearchParamsValueTypes<Params>,
  undefined | null
>;

type ResolveParamResult<
  Key extends string,
  Required extends boolean,
  Params extends ReadonlyArray<{ [N in Key]?: any } | undefined | null | URLSearchParams>,
  Convert extends ((value: any) => any) | undefined = undefined,
> = Required extends true ? ParamValue<Key, Params, Convert> : ParamValue<Key, Params, Convert> | undefined;

/**
 * Gets a parameter value specified by the `key` from the provided params objects.
 *
 * @example
 *   declare const paramSets: [Record<'id', number>, Partial<Record<'id', string>>];
 *
 *   const v = resolveParam({ key: 'id', paramSets }); // string | number | undefined
 *   const v = resolveParam({ key: 'id', paramSets, isRequired: true }); // string | number
 *   const v = resolveParam({ key: 'id', paramSets, isRequired: true, convert: Number }); // number
 *
 * @param key The name of the param value to get from the params objects.
 * @param {Array<{ [key]: *} | undefined>} paramSets Array of params objects
 * @param isRequired Optional, specifies if the param is expected to be present at least in one params object, default `false`.
 * @param convert Optional, function to convert a value, if any, to a required type.
 * @returns The parameter value specified by `key`, if any, otherwise `undefined`.
 */
export const resolveParam = <
  Key extends string,
  Convert extends ((value: any) => any) | undefined = undefined,
  Required extends boolean = false,
  Params extends ReadonlyArray<{ [N in Key]?: any } | undefined | null | URLSearchParams> = Record<Key, any>[],
>({
  key,
  paramSets,
  isRequired = false as Required,
  convert,
}: {
  key: Key;
  paramSets: Params;
  isRequired?: Required;
  convert?: Convert;
}): ResolveParamResult<Key, Required, Params, Convert> => {
  for (const params of paramSets) {
    const value = params instanceof URLSearchParams ? params.get(key) : params?.[key];
    if (value !== undefined && value !== null) return convert ? convert(value) : value;
  }
  invariant(!isRequired, `The param "${key}" is required`);
  return undefined as ResolveParamResult<Key, Required, Params, Convert>;
};
